import * as workerApi from '@company/services/worker/api';
import { useCompanyContext } from '@company/state/companyContext';
import { useDirectMessageContext } from '@company/state/directMessageContext';
import useUpdateWorker from '@company/state/useUpdateWorker/useUpdateWorker';
import {
  formatJobDateRange,
  getJobLocationText,
  getJobRoleAndCompanyInfo,
  getWorkerProfileUrl,
  getWorkerTagsWithHired,
} from '@company/utils/worker-modal-helper';
import { StarRating } from '@components/elements';
import QueryKeys from '@constants/queryKeys';
import useAlert from '@hooks/useAlert';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import IJobDetail from '@th-types/job.detail.type';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import { useCallback, useState } from 'react';
import { AiTwotoneCalendar } from 'react-icons/ai';
import { BiBlock } from 'react-icons/bi';
import { BsChatDots } from 'react-icons/bs';
import { FaHeart, FaRegCopy, FaRegHeart } from 'react-icons/fa6';
import { GrLocation } from 'react-icons/gr';
import { MdOutlineLocationOn } from 'react-icons/md';
import { PiHandsClappingThin } from 'react-icons/pi';
import EndorsementModal from '@company/components/EndorsementModal/EndorsementModal';
import ShareButton from './ShareButton';
import WorkerActions from './WorkerActions';

interface WorkerInfoSectionProps {
  workerData: IWorkerJobData;
  onOpenReviewModal: () => void;
  jobId?: number; // Optional job ID for query invalidation
  showJobDetails?: boolean; // Whether we're in a job context
}

export default function WorkerInfoSection({
  workerData,
  onOpenReviewModal,
  jobId,
  showJobDetails = false,
}: WorkerInfoSectionProps) {
  const [showCopiedToClipboard, setShowCopiedToClipboard] = useState(false);
  const [showEndorsementModal, setShowEndorsementModal] = useState(false);

  // Use props directly - no local state needed
  const isFavoriteWorker = workerData.isFavoritedByCompany || false;
  const isBlockedWorker = workerData.isBlockedByCompany || false;

  const jobAssignmentStatus = useJobAssignmentStatus();
  const { alertMessage, showWarningAlert, showSuccessAlert } = useAlert();
  const { updateWorker } = useUpdateWorker();
  const queryClient = useQueryClient();

  // Favorite worker mutation
  const favoriteMutation = useMutation({
    mutationFn: async (workerId: number) => {
      if (isFavoriteWorker) {
        return await workerApi.removeFavoriteWorker(workerId);
      }
      return await workerApi.favoriteWorker(workerId);
    },
    onSuccess: async (result) => {
      if (result && result.success) {
        await updateWorker({ workerId: workerData.worker.id, jobId });

        const targetQueryKey = `${QueryKeys.WORKER_PROFILE}_${workerData.worker.id}_${jobId || 'no-job'}_triggerProfileView`;
        const jobAssignmentQueryKey = `${QueryKeys.WORKER_PROFILE}_jobAssignment_${workerData.jobAssignment?.id}`;

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.COMPANY_TALENTS],
          }),
          queryClient.invalidateQueries({
            queryKey: [targetQueryKey],
          }),
          queryClient.invalidateQueries({
            queryKey: [jobAssignmentQueryKey],
          }),
        ]);

        showSuccessAlert(result.message);
      } else if (result) {
        showWarningAlert(result.message);
      }
    },
    onError: (error) => {
      console.error('Error toggling favorite worker:', error);
      showWarningAlert('An error occurred while updating favorite status');
    },
  });

  // Block worker mutation
  const blockMutation = useMutation({
    mutationFn: async (workerId: number) => {
      if (isBlockedWorker) {
        return await workerApi.unblockWorker(workerId);
      }
      return await workerApi.blockWorker(workerId);
    },
    onSuccess: async (result) => {
      if (result && result.data?.success) {
        await updateWorker({ workerId: workerData.worker.id, jobId });
        const targetQueryKey = `${QueryKeys.WORKER_PROFILE}_${workerData.worker.id}_${jobId || 'no-job'}_triggerProfileView`;
        const jobAssignmentQueryKey = `${QueryKeys.WORKER_PROFILE}_jobAssignment_${workerData.jobAssignment?.id}`;

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.COMPANY_TALENTS],
          }),
          queryClient.invalidateQueries({
            queryKey: [targetQueryKey],
          }),
          queryClient.invalidateQueries({
            queryKey: [jobAssignmentQueryKey],
          }),
        ]);
        showSuccessAlert(result.message);
      } else if (result) {
        showWarningAlert(result.message);
      }
    },
    onError: (error) => {
      console.error('Error toggling block worker:', error);
      showWarningAlert('An error occurred while updating block status');
    },
  });
  const { openDirectMessageChat, chatId } = useDirectMessageContext();
  const { premium: isPremiumCompany } = useCompanyContext();

  const shareWorkerProfileUrl = useCallback(() => {
    if (!workerData?.worker) return;
    const workerProfileUrl = getWorkerProfileUrl(workerData.worker);
    navigator.clipboard.writeText(workerProfileUrl);
    setShowCopiedToClipboard(true);
    setTimeout(() => setShowCopiedToClipboard(false), 1500);
  }, [workerData?.worker]);

  const handleFavoriteWorker = useCallback(() => {
    favoriteMutation.mutate(workerData.worker.id);
  }, [favoriteMutation, workerData.worker.id]);

  const handleBlockWorker = useCallback(() => {
    blockMutation.mutate(workerData.worker.id);
  }, [blockMutation, workerData.worker.id]);

  const getTags = useCallback(
    (matchSkills?: boolean, hired?: boolean) => {
      if (!workerData?.worker) return [];
      return getWorkerTagsWithHired(workerData.worker, matchSkills, hired);
    },
    [workerData?.worker]
  );

  // Direct message functionality
  const handleOpenDirectMessage = useCallback(() => {
    openDirectMessageChat(workerData.worker, chatId, isPremiumCompany);
  }, [workerData.worker, chatId, isPremiumCompany, openDirectMessageChat]);

  // Job description helper functions
  const getJobDescription = () => {
    if (!workerData?.jobAssignment?.job) return null;

    const jobData = workerData.jobAssignment.job;
    const { isHired } = jobAssignmentStatus(workerData.jobAssignment?.status);

    const addRoleAndCompanyInfo = () => {
      const roleAndCompanyData = getJobRoleAndCompanyInfo(jobData, isHired());
      if (roleAndCompanyData) {
        return (
          <Text component="span" fw={600}>
            <Text component="span" fw={700}>
              {roleAndCompanyData.position}
            </Text>{' '}
            At {roleAndCompanyData.companyName}
          </Text>
        );
      }
      return '';
    };

    const getFormattedDate = () => formatJobDateRange(jobData);
    const getLocationText = () => getJobLocationText(jobData);

    return (
      <Group gap="md" align="center" mb="sm">
        {addRoleAndCompanyInfo()}
        <Group gap="4" align="center">
          <AiTwotoneCalendar size={16} />
          <Text size="sm" fw={600}>
            {getFormattedDate()}
          </Text>
        </Group>
        <Group gap="4" align="center">
          <MdOutlineLocationOn size={16} />
          <Text size="sm" fw={600}>
            {getLocationText()}
          </Text>
        </Group>
      </Group>
    );
  };

  return (
    <Box>
      {/* Job Description */}
      {getJobDescription()}

      <Group align="flex-start" gap="md">
        <Avatar
          src={workerData.worker.profileImageUrl}
          size="160"
          radius="sm"
          style={{ cursor: 'pointer' }}
        />

        {/* Worker Info */}
        <Stack
          gap="xs"
          justify="space-between"
          style={{ flex: 1, minHeight: '160px' }}
        >
          {/* Main Content */}
          <Stack gap="xs" style={{ flex: 1 }}>
            {/* Worker Name */}
            <Group gap="xs" align="center">
              <Text fw={700} size="lg" style={{ textTransform: 'capitalize' }}>
                {workerData.worker.firstName} {workerData.worker.lastName}
              </Text>

              {/* Block Button */}
              <Tooltip
                label={isBlockedWorker ? 'Unblock worker' : 'Block worker'}
              >
                <ActionIcon
                  variant={isBlockedWorker ? 'filled' : 'light'}
                  color={isBlockedWorker ? 'red' : 'gray'}
                  size="lg"
                  onClick={handleBlockWorker}
                  disabled={blockMutation.isPending || isFavoriteWorker}
                  loading={blockMutation.isPending}
                >
                  <BiBlock size={16} />
                </ActionIcon>
              </Tooltip>

              {/* Favorite Button */}
              <Tooltip
                label={
                  isFavoriteWorker
                    ? 'Remove from favorites'
                    : 'Add to favorites'
                }
              >
                <ActionIcon
                  variant="light"
                  color={isFavoriteWorker ? 'green' : 'gray'}
                  size="lg"
                  onClick={handleFavoriteWorker}
                  disabled={favoriteMutation.isPending || isBlockedWorker}
                  loading={favoriteMutation.isPending}
                >
                  {isFavoriteWorker ? (
                    <FaHeart size={16} />
                  ) : (
                    <FaRegHeart size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Group>

            {/* Distance and Rating */}
            <Group gap="md" align="center">
              {workerData.jobAssignment?.distance !== undefined && (
                <Group gap="xs" align="center">
                  <GrLocation size={14} />
                  <Text size="sm" c="dimmed">
                    {workerData.jobAssignment.distance.toFixed(2)} miles
                  </Text>
                </Group>
              )}
              <StarRating value={workerData.worker.rating} color="yellow" />
            </Group>

            {/* Tags */}
            {getTags(
              workerData.jobAssignment?.matchSkills,
              workerData.jobAssignment?.hired
            ).length > 0 && (
              <Group gap="xs">
                {getTags(
                  workerData.jobAssignment?.matchSkills,
                  workerData.jobAssignment?.hired
                ).map((tag) => (
                  <Badge
                    key={tag.label}
                    variant={
                      tag.bg === 'primary' ? 'statusHired' : 'statusApproved'
                    }
                    size="sm"
                  >
                    {tag.label}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>

          {/* Action Buttons - pushed to bottom */}
          <Group gap="md" align="center">
            {/* Share Profile Button */}
            <Tooltip
              label="Link to worker profile copied to clipboard!"
              opened={showCopiedToClipboard}
              position="right"
              withArrow
              arrowSize={5}
            >
              <Group
                gap="8"
                style={{ cursor: 'pointer', width: 'fit-content' }}
                onClick={shareWorkerProfileUrl}
              >
                <Text size="md" c="var(--yellow-dark-20)" fw={600}>
                  Share Profile
                </Text>
                <FaRegCopy size={16} color="var(--yellow-dark-20)" />
              </Group>
            </Tooltip>
          </Group>
        </Stack>

        {/* Action Buttons Section */}
        <Group gap="xs" align="flex-start">
          {/* Always show ENDORSE button */}
          {workerData.isEndorsedByUser ? (
            <Tooltip
              label="This is disabled because you have already endorsed this worker"
              position="bottom"
            >
              <Button
                size="sm"
                variant="light"
                disabled
                leftSection={<PiHandsClappingThin size={18} />}
              >
                ENDORSE
              </Button>
            </Tooltip>
          ) : (
            <Button
              size="sm"
              variant="light"
              leftSection={<PiHandsClappingThin size={18} />}
              onClick={() => setShowEndorsementModal(true)}
            >
              ENDORSE
            </Button>
          )}

          {/* Show job-related or general actions based on context */}
          {showJobDetails && jobId ? (
            <WorkerActions
              workerData={workerData}
              job={
                workerData.jobAssignment?.job || ({ id: jobId } as IJobDetail)
              }
              onOpenReviewModal={onOpenReviewModal}
              jobId={jobId}
            />
          ) : (
            /* General actions when not in job context */
            <>
              <Button
                size="sm"
                variant="light"
                disabled={!isPremiumCompany}
                onClick={handleOpenDirectMessage}
                leftSection={<BsChatDots size={18} />}
              >
                MESSAGE
              </Button>
              <ShareButton
                workerData={workerData}
                jobId={jobId}
                showSuccessAlert={showSuccessAlert}
                showWarningAlert={showWarningAlert}
              />
            </>
          )}
        </Group>
      </Group>

      {/* Alert Messages */}
      {alertMessage.show && (
        <Alert
          color={alertMessage.variant === 'success' ? 'green' : 'red'}
          pos="fixed"
          top={20}
          right={20}
          w={300}
          style={{ zIndex: 1000 }}
        >
          {alertMessage.message}
        </Alert>
      )}

      {/* Endorsement Modal */}
      <EndorsementModal
        show={showEndorsementModal}
        onHide={() => setShowEndorsementModal(false)}
        preselectedWorker={{
          id: workerData.worker.id,
          fullName: `${workerData.worker.firstName} ${workerData.worker.lastName}`,
          email: workerData.worker.email,
          profileImageUrl: workerData.worker.profileImageUrl,
        }}
      />
    </Box>
  );
}
